import { API_ERROR_CODES, CSRF_HEADER } from '@mcikm-vote/shared';
import type { ApiErrorBody } from '@mcikm-vote/shared';

import { leseCsrfWert, setzeCsrfWert, verwerfeCsrfWert } from './csrfSpeicher';
import type { Bereich } from './csrfSpeicher';

/**
 * Anbindung an die API (§23).
 *
 * Aufgaben dieser Hülle:
 *
 * - den CSRF-Wert des jeweiligen Bereichs als Kopfzeile mitschicken (§25.1);
 * - die einheitliche Fehlerstruktur in einen typisierten Fehler wandeln, damit die Oberfläche auf
 *   den Code reagieren kann statt auf Meldungstexte;
 * - bei abgelaufener oder fehlender Sitzung den CSRF-Wert verwerfen, damit kein veralteter Wert
 *   weiterverwendet wird.
 */

/** Fehler einer API-Anfrage mit dem Code aus der einheitlichen Fehlerstruktur (§23.2). */
export class ApiFehler extends Error {
  /**
   * Fehlercode aus der Antwort. Bewusst `string` und nicht der engere Aufzählungstyp: der Server
   * kann in späteren Stufen Codes liefern, die dieses Frontend noch nicht kennt, und die
   * Oberfläche darf daran nicht scheitern.
   */
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiFehler';
  }

  /** Ob die Sitzung fehlt oder abgelaufen ist — die Oberfläche führt dann zur Anmeldung. */
  get istAbgemeldet(): boolean {
    return (
      this.code === API_ERROR_CODES.NICHT_ANGEMELDET ||
      this.code === API_ERROR_CODES.SITZUNG_ABGELAUFEN
    );
  }
}

async function fehlerAus(antwort: Response): Promise<ApiFehler> {
  let code: string = API_ERROR_CODES.INTERNER_FEHLER;
  let message = 'Die Anfrage ist fehlgeschlagen.';

  try {
    const koerper = (await antwort.json()) as Partial<ApiErrorBody>;
    if (koerper.error !== undefined) {
      code = koerper.error.code;
      message = koerper.error.message;
    }
  } catch {
    // Antwort ohne verwertbaren Körper — es bleibt bei der allgemeinen Meldung.
  }

  return new ApiFehler(code, message, antwort.status);
}

export interface AnfrageOptionen {
  /** Welcher Sitzungsbereich — entscheidet über den mitgesendeten CSRF-Wert. */
  bereich?: Bereich;
  methode?: string;
  koerper?: unknown;
  /**
   * Multipart-Körper für einen Dateiupload — bislang nur die Wiederherstellung (§28.2).
   *
   * Schließt {@link koerper} aus: Der Browser setzt bei `FormData` den `Content-Type` samt
   * Trennzeichenfolge selbst. Wer ihn von Hand setzt, zerstört die Trennung, und der Server sieht
   * einen leeren Körper.
   */
  formular?: FormData;
}

/**
 * Führt eine Anfrage aus und gibt die **rohe** Antwort zurück.
 *
 * Für Antworten, die kein JSON sind — etwa den Download des Sicherheitsbackups (§28.2). Fehler
 * werden wie überall in einen {@link ApiFehler} gewandelt.
 */
export async function apiRohantwort(
  pfad: string,
  optionen: AnfrageOptionen = {},
): Promise<Response> {
  const bereich: Bereich = optionen.bereich ?? 'admin';
  const methode = optionen.methode ?? 'GET';
  const kopfzeilen: Record<string, string> = {};

  if (optionen.koerper !== undefined) {
    kopfzeilen['Content-Type'] = 'application/json';
  }

  const csrf = leseCsrfWert(bereich);
  if (csrf !== undefined) {
    kopfzeilen[CSRF_HEADER] = csrf;
  }

  const antwort = await fetch(pfad, {
    method: methode,
    headers: kopfzeilen,
    credentials: 'same-origin',
    ...(optionen.formular === undefined ? {} : { body: optionen.formular }),
    ...(optionen.koerper === undefined ? {} : { body: JSON.stringify(optionen.koerper) }),
  });

  if (!antwort.ok) {
    const fehler = await fehlerAus(antwort);
    if (fehler.istAbgemeldet) {
      verwerfeCsrfWert(bereich);
    }
    throw fehler;
  }

  return antwort;
}

/**
 * Führt eine Anfrage an die API aus.
 *
 * @throws {ApiFehler} bei jeder Antwort außerhalb des Erfolgsbereichs.
 */
export async function apiAnfrage<T>(pfad: string, optionen: AnfrageOptionen = {}): Promise<T> {
  const bereich: Bereich = optionen.bereich ?? 'admin';
  const methode = optionen.methode ?? 'GET';
  const kopfzeilen: Record<string, string> = { Accept: 'application/json' };

  // Bei `FormData` setzt der Browser den Content-Type selbst — samt Trennzeichenfolge.
  if (optionen.koerper !== undefined) {
    kopfzeilen['Content-Type'] = 'application/json';
  }

  const csrf = leseCsrfWert(bereich);
  if (csrf !== undefined) {
    kopfzeilen[CSRF_HEADER] = csrf;
  }

  const antwort = await fetch(pfad, {
    method: methode,
    headers: kopfzeilen,
    // Same-Origin-Betrieb; es gibt keine allgemeine CORS-Freigabe (§23.2).
    credentials: 'same-origin',
    ...(optionen.formular === undefined ? {} : { body: optionen.formular }),
    ...(optionen.koerper === undefined ? {} : { body: JSON.stringify(optionen.koerper) }),
  });

  if (!antwort.ok) {
    const fehler = await fehlerAus(antwort);
    if (fehler.istAbgemeldet) {
      verwerfeCsrfWert(bereich);
    }
    throw fehler;
  }

  if (antwort.status === 204) {
    return undefined as T;
  }

  const ergebnis = (await antwort.json()) as T;

  // Trägt die Antwort einen frischen CSRF-Wert, wird er übernommen.
  const mitCsrf = ergebnis as { csrfToken?: unknown };
  if (typeof mitCsrf.csrfToken === 'string') {
    setzeCsrfWert(bereich, mitCsrf.csrfToken);
  }

  return ergebnis;
}
